// src/components/Modals/AskQuestionModal.js
import React, { useState, useEffect, useContext } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { AuthContext } from '../../context/AuthContext';

const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : Promise.resolve(null);

const ThemedCloseIcon = () => (
  <svg aria-hidden="true" width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      d="M5 5L15 15M15 5L5 15"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
    />
  </svg>
);

const AskQuestionModal = ({ onClose, onQuestionPosted, recipient, useProfileTheme = false }) => {
  // We expect recipient to have a full user object, including _id.
  const { currentUser } = useContext(AuthContext);

  const [visible, setVisible] = useState(false);
  const [question, setQuestion] = useState('');
  const [description, setDescription] = useState('');
  const [donationAmount, setDonationAmount] = useState(0);
  const [selectedDonation, setSelectedDonation] = useState(null);
  const [selectedDonationCustomValue, setSelectedDonationCustomValue] = useState("");
  const [error, setError] = useState(null);
  const [tipOptions, setTipOptions] = useState([
    { id: 1, value: "$0", removable: false },
    { id: 2, value: "$5", removable: true },
    { id: 3, value: "$25", removable: true },
    { id: 4, value: "$50", removable: true },
    { id: 5, value: "Custom", removable: true },
  ]);
  const [yourName, setYourName] = useState('');
  const [yourEmail, setYourEmail] = useState('');

  // Track whether they’ve agreed
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);

  // Fetch tip options for the recipient using their _id
  useEffect(() => {
    if (visible && recipient && (recipient._id || recipient.id)) {
      const recipientId = recipient._id || recipient.id;
      console.log('AskQuestionModal: fetching tipOptions for userId:', recipientId);
      fetch(`/api/payments/tipOptions?userId=${recipientId}`)
        .then(res => res.json())
        .then(data => {
          console.log('Fetched tip options:', data.tipOptions);
          if (data.tipOptions) {
            setTipOptions(data.tipOptions);
          }
        })
        .catch(err => {
          console.error('Error fetching tip options:', err);
        });
    }
  }, [visible, recipient]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(() => onClose(), 600);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Debug log: show current user and recipient
    console.log('Current user:', currentUser);
    console.log('Recipient:', recipient);

    if (!question.trim()) {
      setError('Question title is required');
      return;
    }
    // Enforce the terms checkbox for anonymous users
    if (!currentUser && !agreed) {
      setError('You must agree to the Terms of Use and Privacy Policy before posting.');
      return;
    }
    if (!currentUser && (!yourName.trim() || !yourEmail.trim())) {
      setError('Name and Email are required');
      return;
    }
    if (!recipient || !(recipient._id || recipient.id)) {
      setError('Recipient user data is missing. Please try again later.');
      return;
    }

    // Explicitly convert the recipient's _id to a string.
    const recipientId = (recipient._id || recipient.id).toString();
    console.log('AskQuestionModal: final recipient with _id:', recipientId);

    // Ensure we are using recipient.email as the toUserEmail
    const toUserEmail = recipient.email;

    if (!toUserEmail) {
      setError('Recipient email is missing');
      return;
    }

    // Require tip if tipping is enabled
    if (recipient.tippingEnabled !== false) {
      const hasCustom = selectedDonationCustomValue.trim() !== "";
      const hasValidCustomAmount = hasCustom && donationAmount >= 1;
      const hasPreset = selectedDonation !== null;

      if (!hasPreset && !hasValidCustomAmount) {
        setError("Select a tip amount to proceed");
        return;
      }
    }

    // Build questionDataPayload ensuring we include the recipient ID and toUserEmail.
    const questionDataPayload = {
      question,
      detail: description,
      tip: donationAmount,
      isCurated: false,
      toUsername: recipient.username,  // recipient’s username
      recipientName: recipient.name,     // recipient’s name
      toUserId: recipientId,
      fromUsername: currentUser ? currentUser.username : 'guest',
      askedName: currentUser ? currentUser.name : yourName,
      askedEmail: currentUser ? currentUser.email : yourEmail, // explicitly set askedEmail
      yourName: currentUser ? currentUser.name : yourName,
      yourEmail: currentUser ? currentUser.email : yourEmail,
      toUserEmail: recipient.email,
    };


    // Check custom tip amount for minimum of $1
    if (selectedDonation === null && selectedDonationCustomValue.trim() !== "" && donationAmount < 1) {
      setError("Your tip can't be a penny, please start with at least one buck.");
      return;
    }
    console.log('Built questionDataPayload:', questionDataPayload);

    if (donationAmount > 0) {
      // Tip-based: create a Checkout Session.
      try {
        const checkoutPayload = {
          donationAmount,
          userId: currentUser ? currentUser._id : 'guest',
          questionData: questionDataPayload,
        };

        console.log("Creating checkout session with payload:", checkoutPayload);
        const res = await fetch('/api/checkout/create-checkout-session', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(checkoutPayload),
        });
        const data = await res.json();
        console.log("Checkout session response:", data);

        if (!res.ok) {
          setError(data.message || 'Failed to create checkout session');
          return;
        }

        // Log to check if paymentIntentId is returned
        console.log("Returned Payment Intent ID from backend:", data.paymentIntentId);

        // Assuming you have the PaymentIntent ID now returned from the backend
        const paymentIntentId = data.paymentIntentId; // Assuming you return the paymentIntentId from your backend
        if (!paymentIntentId) {
          console.log("No paymentIntentId received");
        }

        // Optionally store the paymentIntentId in state if you need it later
        // setPaymentIntentId(paymentIntentId); // Uncomment if you want to store it in state

        if (data.url) {
          window.location.assign(data.url);
          return;
        }

        const stripe = await stripePromise;
        if (!stripe) {
          setError('Payment checkout is temporarily unavailable. Please try again.');
          return;
        }

        const result = await stripe.redirectToCheckout({ sessionId: data.sessionId });
        if (result.error) {
          console.error("Stripe redirect error:", result.error);
          setError(result.error.message);
        }
      } catch (err) {
        console.error("Error in checkout flow:", err);
        setError('Failed to initiate payment. Please try again.');
      }
    } else {
      // No tip: post question directly.
      try {
        console.log("Posting question with payload:", questionDataPayload);
        const res = await fetch('/api/questions', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(questionDataPayload),
        });
        const data = await res.json();
        if (!res.ok) {
          setError(data.message || 'Failed to post question');
        } else {
          if (onQuestionPosted) onQuestionPosted(data);
          handleClose();
        }
      } catch (err) {
        console.error("Error posting question:", err);
        setError('Failed to post question. Please try again.');
      }
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target.classList.contains('modal')) {
      handleClose();
    }
  };

  return (
    <div
      className={`modal${useProfileTheme ? ' modal-profile-theme' : ''} ${visible ? 'show' : ''}`}
      onClick={handleOverlayClick}
    >
      <div className="card modal-card" onClick={(e) => e.stopPropagation()}>
        <span className="close-modal" onClick={handleClose}>
          {useProfileTheme ? <ThemedCloseIcon /> : <img src="/images/i_modal_close.svg" alt="Close" />}
        </span>
        <form className="padding-top-m" onSubmit={handleSubmit}>
          {/* Question Title */}
          <div className="text-uppercase">
            Question title <span className="red">*</span>
          </div>
          <input
            type="text"
            name="question"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            required
          />

          {/* Description */}
          <div className="text-uppercase margin-top-l">Description</div>
          <textarea
            name="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          {/* Name & Email for Guests */}
          {!currentUser && (
            <>
              <div className="text-uppercase margin-top-l">
                Your Name <span className="red">*</span>
              </div>
              <input
                type="text"
                name="yourName"
                value={yourName}
                onChange={(e) => setYourName(e.target.value)}
                required
              />
              <div className="text-uppercase margin-top-l">
                Email <span className="red">*</span>
              </div>
              <input
                type="email"
                name="yourEmail"
                value={yourEmail}
                onChange={(e) => setYourEmail(e.target.value)}
                required
              />
            </>
          )}

          {/* Tip options & rest of form */}
          {(recipient.tippingEnabled !== false) && (
            <div className="tip">
              <div className="text-uppercase margin-top-l margin-bottom-m">
                Tip amount
              </div>
              <div className="container-fluid">
                <div className="row">
                  {tipOptions.map((option) => {
                    if (option.value.toLowerCase() === "custom") {
                      return (
                        <div className="col-xs" key={option.id}>
                          <input
                            type="number"
                            name="otherAmount"
                            placeholder="$"
                            value={selectedDonationCustomValue}
                            onChange={(e) => {
                              const val = Number(e.target.value);
                              setSelectedDonation(null);
                              setDonationAmount(val);
                              setSelectedDonationCustomValue(e.target.value);
                            }}
                          />
                        </div>
                      );
                    }
                    const numericValue = Number(option.value.replace('$', ''));
                    return (
                      <div className="col-xs" key={option.id}>
                        <button
                          type="button"
                          className={`donation-button ${selectedDonation === option.id ? 'selected' : ''}`}
                          onClick={() => {
                            setSelectedDonation(option.id);
                            setDonationAmount(numericValue);
                            setSelectedDonationCustomValue("");
                          }}
                        >
                          {option.value}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Error message (optional: keep outside the row or inside as needed) */}
          {error && <p className="fail margin-top-s">{error}</p>}

          {/* Terms & Privacy and Submit Button side-by-side */}
          <div className="modal-footer">
            {/* Column 1: checkbox */}
            {!currentUser && (
              <div className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  id="agreeBox"
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  required
                  aria-labelledby="agreeText"
                />
                <p id="agreeText" style={{ margin: 0 }}>
                  I agree to the{' '}
                  <a href="/terms-of-use" target="_blank" rel="noopener noreferrer">Terms of Use</a>{' '}
                  and{' '}
                  <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
                </p>
              </div>
            )}
            {/* Column 2: submit button */}
            <button className="right margin-top-l-sm" type="submit">Post Question</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AskQuestionModal;
